<template>
  <div class="simpleProductContainer">
    <div class="simpleProductBadgeAndTextContainer">
      <div :class="{simpleProductBadgeNone: !hasPurchasableUnits}" class="simpleProductBadge">{{ data.quantity }}</div>
      <div class="simpleProductBadgeTextContainer">
        <div v-if="data.isAddOn" class="simpleProductBadgeText">
          {{ strings.badgeTextAvailable }}
        </div>
        <div v-else class="simpleProductBadgeText">
          {{strings.tdc_reservedForYou}}
        </div>
      </div>
    </div>
    <ItemCartFrame
      :showPrices="hasPurchasableUnits"
      :sku="data.sku"
    />
    <div v-if="hasPurchasableUnits" class="quantitySelectorContainer">
      <ns-quantity-selector
        :id="'SPQuantitySelector_' + data.sku"
        @quantity-changed="handleSelect"
        :max="data.quantity"
        :open-modal="isTouchDevice"
        class="simpleProductQuantityDropdown"
      />
      <div
        :class="{simpleProductQuantityTotalDisabled: !hasPurchasableUnits}"
        class="simpleProductQuantityTotal"
        :id="'SPQuantity_' + data.sku"
      >
        / {{ data.quantity }}
      </div>
    </div>
    <div v-if="!hasPurchasableUnits" class="simpleProductLimitReached">
      {{ strings.limitReached }}
    </div>
    <event-button
      :qualification="data"
      :product="data"
      @click="addToCart"
    />
  </div>
</template>

<script>
import {
  mapGetters,
  mapMutations,
} from 'vuex'

import {
  events,
  RunConfigService,
  util,
} from '@nuskin/ns-util'

import config from '../../js/qualificationsConfigService'
import EventButton from '../events/EventButton.vue'
import ItemCartFrame from './ItemCartFrame.vue'

export default {
  components: {
    EventButton,
    ItemCartFrame,
  },
  computed: {
    data() {
      if (this.product && !this.addOn) {
        return this.product
      }
      return this.addOn
    },
    hasPurchasableUnits() {
      return this.data.quantity > 0
    },
    ...mapGetters({
      getSelectedValue: ['qualifications/addOnSelections/selection']
    }),
    selected: {
      get() {
        const payload = {
          baseSku: this.product.sku,
          addOnSku: this.addOn.sku
        }
        const selectedValue = this.getSelectedValue(payload)
        return this.hasPurchasableUnits ?
          selectedValue :
          '0'
      },
      set(value) {
        const payload = {
          baseSku: this.product.sku,
          addOnSku: this.addOn.sku,
          selection: value
        }
        this.saveSelection(payload)
      }
    },
  },
  data() {
    return {
      // String Variables
      strings: {
        addToCart: config.getTdcString('tdc_addOnAddToCart'),
        badgeTextAvailable: config.getTdcString('tdc_addOnBadgeTextAvailable'),
        badgeTextConditionally: config.getTdcString('tdc_addOnBadgeTextConditionally'),
        quantityLabel: config.getTdcString('tdc_addOnQuantityLabel'),
        limitReached: config.getTdcString('tdc_limitReached').toUpperCase(),
        tdc_reservedForYou: config.getTdcString('tdc_reservedForYou'),
      },
      isTouchDevice: util.MobileUtils.isMobileLike(),
    }
  },
  methods: {
    ...mapMutations({
      saveSelection: 'qualifications/addOnSelections/saveSelection'
    }),
    addToCart() {
      const selected = this.selected
      if (!selected || selected < 1) {
        return
      }
      const options = {
        cntryCd: RunConfigService.getRunConfig().country,
        language: RunConfigService.getRunConfig().language,
        userId: window.user ? window.user.id : null,
        qty: selected,
        region: null,
        isAdr: false,
        sku: this.addOn.sku,
      }
      aem.CartService.addSkuToCart(options).catch((err) => {
        if (err instanceof aem.NsNotQualifiedException) {
          events.publish(events.shop.SHOW_EXCLUSIVE_OFFER_DETAILS, {
            sku: this.addOn.sku,
          })
        }
      })
    },
    handleSelect(event) {
      this.selected = event.detail.value
    },
  },
  mounted() {
    if (!this.addOn) {
      this.addOn = this.product
    }
  },
  props: {
    addOn: Object,
    product: Object,
  },
}
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";

$mermaid: map.get(colors.$baseColors, "mermaid");
$dryink: map.get(colors.$baseColors, "dryink");

section {
  font-family: 'proxima-nova';
  .simpleProductBadgeAndTextContainer {
    align-items: center;
    display: flex;
    margin-top: 20px;
    .simpleProductBadge {
      align-items: center;
      align-self: flex-start;
      background-color: $mermaid;
      border-radius: 50%;
      color: white;
      display: flex;
      font-size: 20px;
      font-weight: 600;
      height: 30px;
      justify-content: center;
      margin-right: 15px;
      min-width: 30px;
      width: 30px;
    }
    .simpleProductBadgeNone {
      background-color: $dryink;
      font-weight: 600;
    }
    .simpleProductBadgeTextContainer {
      .simpleProductBadgeText {
        color: #272525;
        font-size: 16px;
        font-weight: 700;
        text-align: left;
      }
    }
  }
  .simpleProductItemFrame {
    margin-top: 15px;
  }
  .quantitySelectorContainer {
    align-items: center;
    display: flex;
    justify-content: center;
    margin-top: 15px;
    .quantitySelectorText {
      margin-right: 10px;
    }
    .quantitySelectorTextDisabled {
      color: #BBBCBC;
    }
    .simpleProductQuantityTotal {
      font-weight: 700;
      margin-left: 5px;
    }
    .simpleProductQuantityTotalDisabled {
      color: #BBBCBC;
    }
  }
  .simpleProductLimitReached {
    color: #272525;
    font-family: 'proxima-nova';
    font-size: 13px;
    font-weight: 600;
    margin-left: 45px;
    margin-top: 15px;
    text-align: left;
  }
  .simpleProductAddToCartButton {
    margin-top: 15px;
  }
}
</style>

<style lang="scss">
.quantitySelectorContainer {
  .ns-slotted-dropdown {
    .dropdown-area {
      .current-selection {
        height: 25px;
        min-height: 25px;
      }
    }
  }
}
</style>
